import type { Lesson } from "@edu/shared";
import { absText, agoLabel, dayDiff, nextLabel, type WhenLabel } from "../../lib/when.ts";

/**
 * Колонка «Дата» списка уроков: что в ней написано и в каком порядке идут
 * строки.
 *
 * Обе половины живут вместе намеренно: они считают ОДИН и тот же момент. Ярлык
 * его показывает, сортировка по нему выстраивает, и разъехавшись, они дадут
 * колонку, отсортированную не по тому, что в ней видно. Модуль отдельно от
 * компонентов ради тестов: это чистые функции, а раздел 9 CLAUDE.md требует их
 * покрывать.
 */

/**
 * Момент занятия.
 *
 * ⚠️ **Зовут её ТОЛЬКО на датированных уроках**, поэтому `?? lesson.createdAt`
 * это значение ради типа, а не ради поведения: `scheduledAt` в контракте и
 * `null`, и вовсе отсутствующее поле (старый API), а числа тут требует
 * сортировка. Уроки без даты до этой функции не доходят вовсе: `byWhen`
 * отделяет их первой же строкой и выстраивает по `createdAt` напрямую.
 *
 * ⚠️ Так что подмены дня занятия днём создания здесь НЕТ, и запрет из шапки
 * `Lesson.scheduledAt` («подставлять вместо него `createdAt` запрещено: это
 * выдуманные данные») мы не нарушаем ни в порядке строк, ни на экране: у такого
 * урока в колонке стоит «без даты».
 */
function whenKey(lesson: Lesson): number {
  return lesson.scheduledAt ?? lesson.createdAt;
}

/** Есть ли у урока время занятия вовсе. */
function scheduled(lesson: Lesson): boolean {
  return (lesson.scheduledAt ?? null) !== null;
}

/**
 * Что стоит в колонке «Дата».
 *
 * ⚠️ **Дата ЗАНЯТИЯ, а не создания, и до 17.09.2026 было наоборот.** Тогда урок,
 * заводимый человеком, времени не имел вовсе, и день создания был единственным,
 * который у строки есть. Теперь такой урок всегда встаёт на дату и время, и
 * «сегодня» у занятия, назначенного на субботу, читалось бы как ошибка списка.
 *
 * Прошлое и будущее разведены двумя ярлыками намеренно: у занятия на этой
 * неделе человек читает день недели («чт 18:30»), а у прошедшего ему нужно «3
 * дня назад». Тона считает `lib/when.ts`, и здесь тон ставится ровно один раз,
 * в ветке абзацем ниже.
 *
 * ⚠️ **Урок без даты говорит про отсутствие даты, а не про день создания.**
 * Такие уроки остались: их завела прежняя форма этого экрана, пачка по галочкам
 * и всё, что старше расписания; ради них раздел «Уроки» и существует отдельно
 * от сетки (`features/shell/SideNav.tsx`). Путь тут был в два шага. Сначала в
 * колонке стоял день создания на языке дня занятия («сегодня 14:22», тон
 * `accent`), то есть колонка на двух соседних строках означала разное, а
 * отличие уезжало в подсказку под курсором, которой на телефоне нет вовсе.
 * Потом там же встало «создан 17 сент.», и слово столкнулось с пилюлей
 * «создан» в колонке «Состояние» той же строки: на узкой раскладке подписи
 * подставляются из `data-label`, и строка читалась «Дата: создан 17 сент. …
 * Состояние: создан». Теперь в колонке стоит ФАКТ, который человеку и нужен:
 * этого урока нет в расписании. День заведения целиком уехал в подсказку и
 * называет себя там своим именем, потому что в строке этого слова больше нет.
 *
 * Осиротевший правкой расписания урок в эту ветку НЕ попадает: `scheduled_at`
 * после создания никто не обнуляет, и дату занятия он сохраняет.
 */
export function lessonWhenLabel(lesson: Lesson, now: number): WhenLabel {
  const at = lesson.scheduledAt ?? null;
  if (at !== null) return at > now ? nextLabel(at, now) : agoLabel(at, now);
  return { text: "без даты", tone: "none", title: `Создан ${absText(lesson.createdAt)}` };
}

/** Направление внутри группы: будущее читают сверху вниз, прошлое наоборот. */
type Dir = "asc" | "desc";

/**
 * Порядок внутри группы по тому моменту, который в колонке и ПОКАЗАН.
 *
 * ⚠️ **Раньше порядок не трогали вовсе**, и довод был такой: он приехал с
 * сервера, а переставлять его на клиенте значит спорить с пагинацией, когда она
 * появится. Довод снят 17.09.2026 по факту: пагинации нет, а сервер отдаёт
 * уроки по `created_at DESC`, тогда как в колонке с того же дня стоит дата
 * ЗАНЯТИЯ. Даты от этого скакали: занятие на октябрь, заведённое сегодня,
 * вставало выше завтрашнего, заведённого вчера.
 *
 * ⚠️ **С 26.09.2026 эта сортировка НЕ касается «Завершённых».** Они приезжают
 * страницами, и порядок им ставит сервер ключом `coalesce(scheduled_at,
 * created_at) desc, id desc` (`repo/lesson-list.ts` в API), тем же, по которому
 * режет курсор. Пересортировка здесь клала бы догруженную страницу строками
 * между уже показанными, поэтому «Завершённые» идут в порядке сервера, а новая
 * страница дописывается в конец. Урок без даты встаёт там по дню заведения среди
 * датированных, это принято. Правило «без даты в конец» ниже теперь про «Идут» и
 * «Созданные».
 *
 * ⚠️ **Потолок незавершённых при этом не безобиден, и клиентской сортировкой он
 * не лечится.** Они приезжают целиком под потолком в двести уроков по дате
 * СОЗДАНИЯ (`LESSON_ACTIVE_LIMIT`, упор назван полем `truncated`), а порядок мы
 * строим по дате занятия: у упёршейся в потолок половины ближайшее занятие может
 * не приехать ВОВСЕ. Чинится это только на сервере, и до тех пор список честен
 * ровно в тех пределах, что приехали.
 *
 * ⚠️ **Урок без даты уходит в КОНЕЦ своей группы, и это правило одно на все
 * группы.** Места в хронологии занятий у него нет вовсе: его ключ это день
 * создания, то есть среди будущего он всплыл бы наверх, заняв место ближайшего
 * занятия, а среди прошлого встал бы между двумя «вчера» строкой без даты в
 * колонке. Второе увидели глазами на стенде 17.09.2026, в группе «Завершённые»:
 * по ключу законно, читается как дырка в сортировке. Между собой такие уроки
 * идут свежими сверху.
 *
 * ⚠️ **Возрастающая группа поверх этого делится надвое: «предстоит» и
 * «прошло».** Одним возрастанием её сортировать нельзя, и это находка ревью, а
 * не вкусовщина: «Созданные» это `status !== live && !== ended`, то есть группа
 * с прошлым внутри. Урок рождается `created` и выходит оттуда только входом в
 * комнату (уборщик брошенных `created` не трогает намеренно), а в режиме
 * сосуществования (раздел 2 CLAUDE.md) в нашу комнату не входят вовсе: занятие
 * идёт в Zoom. У такого преподавателя через месяц НИ ОДИН урок не покинет
 * группу, и чистое возрастание положило бы наверх занятие полугодовой давности,
 * а ближайшее утопило бы в середине. Поэтому сначала то, что предстоит, по
 * возрастанию (в 23:00 наверху там стоит сегодняшнее утреннее занятие, а не
 * ближайшее по часам: день читается сверху вниз целиком); следом то, чьё время
 * прошло, а урок так и остался созданным, свежим сверху. Монотонности сверху
 * вниз у группы нет, и это честнее монотонности, которая прячет сегодняшний
 * день в середине списка.
 *
 * ⚠️ **Граница этих двух кусков это НАЧАЛО МЕСТНЫХ СУТОК, а не текущая
 * минута.** Считает её `dayDiff`, то есть ровно то, чем ярлык выбирает свои
 * слова: всё, что подписано «сегодня», лежит в «предстоит», и кусок с колонкой
 * обещают одно и то же по построению, а не по совпадению. С границей по `now`
 * занятие в 18:29 стояло первой строкой как ближайшее, а в 18:31 уезжало под
 * десяток будущих, то есть ровно в ту минуту, когда на нём жмут «Войти» (а
 * покинуть `created` в режиме сосуществования оно не может). Вдобавок тон
 * «занятие сегодня» встречался сразу в двух кусках, глазом неразличимых.
 * Побочная выгода: порядок больше не зависит от текущей секунды, граница едет
 * раз в сутки, и строка не переставится под курсором от постороннего нажатия.
 */
function byWhen(items: Lesson[], now: number, dir: Dir): Lesson[] {
  // `filter` уже отдаёт свои массивы, поэтому сортировка на месте ничего чужого
  // не переставляет. Спред ниже это СКЛЕЙКА кусков, а не защитная копия: своей
  // копии сортировке не нужно ни в одной ветке
  const dated = items.filter((l) => scheduled(l));
  const undated = items.filter((l) => !scheduled(l)).sort((a, b) => b.createdAt - a.createdAt);
  if (dir === "desc") return [...dated.sort((a, b) => whenKey(b) - whenKey(a)), ...undated];
  const ahead = dated
    .filter((l) => dayDiff(whenKey(l), now) >= 0)
    .sort((a, b) => whenKey(a) - whenKey(b));
  const past = dated
    .filter((l) => dayDiff(whenKey(l), now) < 0)
    .sort((a, b) => whenKey(b) - whenKey(a));
  return [...ahead, ...past, ...undated];
}

export interface LessonGroup {
  key: string;
  title: string;
  items: Lesson[];
}

/**
 * Группировка по состоянию: идущее занятие обязано быть сверху, завершённое
 * внизу. До этого список шёл вперемешку в порядке сервера, и урок, который
 * прямо сейчас идёт, приходилось искать глазами среди завершённых.
 *
 * Направления разные и следуют из того, как человек читает время: у созданных
 * он ищет ближайшее («что у меня дальше»), а у остальных свежее («что было
 * только что»). У «Завершённых» свежее сверху тоже, но порядок им ставит
 * сервер, и здесь он не трогается (разбор у `byWhen`). Пустые группы не рисуем:
 * заголовок без строк это обещание, что где-то есть скрытое.
 *
 * ⚠️ `now` приезжает снаружи и берётся один раз на отрисовку списка: порядок и
 * ярлыки строк обязаны считаться от ОДНОГО дня, иначе занятие, попавшее на
 * полночь, встало бы в куске «предстоит» с ярлыком «вчера 23:50».
 */
export function groupLessons(lessons: Lesson[], now: number): LessonGroup[] {
  return [
    {
      key: "live",
      title: "Идут",
      items: byWhen(
        lessons.filter((l) => l.status === "live"),
        now,
        "desc",
      ),
    },
    {
      key: "open",
      title: "Созданные",
      // Всё, что не идёт и не завершено. Статуса может не быть вовсе: поле
      // опционально в контракте, и урок без него это тоже «созданный»
      items: byWhen(
        lessons.filter((l) => l.status !== "live" && l.status !== "ended"),
        now,
        "asc",
      ),
    },
    {
      key: "ended",
      title: "Завершённые",
      // Порядок сервера, без пересортировки: разбор у `byWhen`
      items: lessons.filter((l) => l.status === "ended"),
    },
  ].filter((g) => g.items.length > 0);
}
